import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { money } from "@/lib/billing";
import { Card } from "@/components/PortalPage";

const when = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");

const Diff = ({ c }) => {
  if (!c) return <p className="text-[11px] text-[#777]">First version.</p>;
  const parts = [
    ...c.added.map((x) => `Added ${x}`), ...c.removed.map((x) => `Removed ${x}`), ...c.changed.map((x) => `${x.item}: ${x.from} → ${x.to}`),
    ...(c.total_from !== c.total_to ? [`Total ${money(c.total_from)} → ${money(c.total_to)}`] : []), ...(c.terms_changed ? ["Terms or notes changed"] : []),
    ...(c.pricing_terms_changed.length ? [`Changed: ${c.pricing_terms_changed.join(", ").replace(/_/g, " ")}`] : []), ...(c.title_changed ? ["Title changed"] : []),
  ];
  return <ul className="list-disc pl-4 text-[11px] text-[#C9C9C9]">{parts.length ? parts.map((p, i) => <li key={i}>{p}</li>) : <li>No price, scope or terms change</li>}</ul>;
};

export const QuoteVersions = ({ doc, onChange }) => {
  const [rows, setRows] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const accepted = !!(doc.approved_by || doc.acceptance?.override_at);
  useEffect(() => { api(`/billing/docs/${doc.id}/revisions`).then(setRows).catch(() => setRows([])); }, [doc.id, doc.revision, doc.status]);
  const reopen = async () => {
    if (!window.confirm("Reopen this accepted quote? The signed version is kept in history, any unpaid deposit invoice is voided, and the customer must sign the new version.")) return;
    setBusy(true);
    try { onChange(await api(`/billing/docs/${doc.id}/reopen`, { method: "POST", body: { reason } })); setReason(""); toast.success("Reopened. Make your changes, then send it for re-approval."); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  if (!rows) return null;
  return (
    <Card className="mt-4 space-y-3" data-testid="quote-versions">
      <p className="text-xs font-extrabold tracking-[1.5px] text-neutral-400">VERSIONS</p>
      {rows.map((r) => (
        <div key={r.revision} className="rounded-lg bg-[#121212] p-2.5" data-testid={`quote-version-${r.revision}`}>
          <p className="text-[12px] font-bold">Version {r.revision}{r.current ? " (current)" : ""} · {money(r.total)} <span className="font-normal text-[#888]">· {r.status}{r.saved_at ? ` · saved ${when(r.saved_at)}` : ""}{r.by ? ` by ${r.by}` : ""}</span></p>
          {r.accepted_by && <p className="text-[11px] text-[#4CCC06]">Signed by {r.accepted_by} · {when(r.accepted_at)}</p>}
          {r.reopen_reason && <p className="text-[11px] text-[#F59E0B]">Reopened for re-approval: {r.reopen_reason}</p>}
          <Diff c={r.changes} />
          {r.material && !r.current && <p className="text-[10px] font-bold text-[#F59E0B]">Price, scope or terms changed: needs the customer's signature</p>}
        </div>
      ))}
      {accepted && !doc.job_id && (
        <div className="space-y-2 border-t border-[#262626] pt-3" data-testid="quote-reopen-box">
          <p className="text-[12px] text-[#C9C9C9]">Need to change price, scope or terms? Reopen it for re-approval. The customer signs the new version.</p>
          <input className="w-full rounded-lg border border-[#333] bg-[#121212] px-3 py-2 text-sm" placeholder="Reason (required), e.g. customer added two doors" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="quote-reopen-reason" />
          <button disabled={busy || reason.trim().length < 5} onClick={reopen} data-testid="quote-reopen-btn" className="w-full rounded-lg border border-[#F59E0B] py-2.5 text-xs font-extrabold tracking-[1px] text-[#F59E0B] disabled:opacity-40">REOPEN FOR RE-APPROVAL</button>
        </div>
      )}
      {accepted && doc.job_id && <p className="text-[11px] text-[#A0A0A0]" data-testid="quote-use-change-order">This quote is now a job. Add a change order on the job for any changes.</p>}
    </Card>
  );
};
